import React, { useEffect, useState } from "react";
import { fetchOverview, fetchTenants } from "../services/api";
import { formatZAR } from "../services/plans";
import { usePacks, tenantMonthlyCents, PACK_GROUP_LABELS, PACK_GROUP_ORDER } from "../services/packs";
import type { PlatformOverview, Tenant } from "../types";
import { usePageTitle } from "../hooks/usePageTitle";

export function AnalyticsPage() {
  usePageTitle("Analytics");
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const { bySlug } = usePacks();

  useEffect(() => {
    fetchOverview().then(setOverview);
    fetchTenants().then(setTenants);
  }, []);

  if (!overview) return <div className="muted">Loading…</div>;

  // MRR split by package group, computed from active tenants + their real
  // per-rep package price (mirrors the backend MRR logic). A "No package"
  // bucket catches any active tenant not yet on a pack.
  const activeTenants = tenants.filter((t) => t.status === "active");
  const packMrr = PACK_GROUP_ORDER.map((g) => {
    const inGroup = activeTenants.filter((t) => t.pack && bySlug[t.pack]?.group === g);
    const mrr = inGroup.reduce((s, t) => s + tenantMonthlyCents(t, bySlug[t.pack!]?.perRepMonthlyCents ?? null, bySlug[t.pack!]?.flatMonthlyCents ?? null), 0);
    return { key: g as string, label: PACK_GROUP_LABELS[g], count: inGroup.length, mrr };
  });
  const noPack = activeTenants.filter((t) => !t.pack || !bySlug[t.pack]);
  if (noPack.length) {
    packMrr.push({
      key: "none",
      label: "No package",
      count: noPack.length,
      mrr: noPack.reduce((s, t) => s + tenantMonthlyCents(t, null), 0)
    });
  }
  const breakdownTotal = packMrr.reduce((s, r) => s + r.mrr, 0);
  const totalVoice = tenants.reduce((s, t) => s + t.usage.voiceMinutesThisMonth, 0);
  const totalWa = tenants.reduce((s, t) => s + t.usage.whatsappMessagesThisMonth, 0);
  const totalAi = tenants.reduce((s, t) => s + t.usage.aiTokensThisMonth, 0);

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Analytics</span>
          <h1>Platform analytics</h1>
          <p className="muted">Revenue, plan mix, and usage across the fleet.</p>
        </div>
      </header>

      <div className="grid-3">
        <div className="card kpi">
          <h3>Voice minutes (this month)</h3>
          <div className="value tnum">{Intl.NumberFormat("en-ZA").format(totalVoice)}</div>
        </div>
        <div className="card kpi">
          <h3>WhatsApp messages (this month)</h3>
          <div className="value tnum">{Intl.NumberFormat("en-ZA").format(totalWa)}</div>
        </div>
        <div className="card kpi">
          <h3>AI tokens (this month)</h3>
          <div className="value tnum">{Intl.NumberFormat("en-ZA", { notation: "compact" }).format(totalAi)}</div>
        </div>
      </div>

      <div className="card">
        <div className="section-head"><h2>MRR by package</h2><span className="muted tnum">{formatZAR(breakdownTotal)} total</span></div>
        <div className="col" style={{ gap: 12 }}>
          {packMrr.map((row) => {
            const pct = (row.mrr / Math.max(1, breakdownTotal)) * 100;
            return (
              <div key={row.key} className="meter-row">
                <div className="meter-label">{row.label}</div>
                <div className="meter"><span style={{ width: `${pct}%` }} /></div>
                <div className="meter-value tnum">{formatZAR(row.mrr)} <span className="muted">({row.count})</span></div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="section-head"><h2>Top usage tenants</h2></div>
        <div className="table-wrap" style={{ border: 0 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>Voice min</th>
                <th>WhatsApp msg</th>
                <th>AI tokens</th>
              </tr>
            </thead>
            <tbody>
              {[...tenants].sort((a, b) =>
                (b.usage.voiceMinutesThisMonth + b.usage.whatsappMessagesThisMonth) -
                (a.usage.voiceMinutesThisMonth + a.usage.whatsappMessagesThisMonth)
              ).slice(0, 6).map((t) => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td className="tnum">{Intl.NumberFormat("en-ZA").format(t.usage.voiceMinutesThisMonth)}</td>
                  <td className="tnum">{Intl.NumberFormat("en-ZA").format(t.usage.whatsappMessagesThisMonth)}</td>
                  <td className="tnum">{Intl.NumberFormat("en-ZA", { notation: "compact" }).format(t.usage.aiTokensThisMonth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
